/*
 * One Click Upsell (Zipify) slide cart drawer enhancements.
 * The drawer is rendered by the app, so this script only observes it and toggles
 * state classes used by assets/component-ocu-cart-drawer.css:
 * is-reached on each reward tier, is-next on the first tier not yet reached,
 * is-just-reached when a tier is reached during the current session,
 * is-complete on the reward bar when every tier is reached, is-revealing when
 * the bar scrolls into view (drawer opens), and is-celebrating when the final
 * goal is reached during the current session.
 * It also sizes each tier segment by its threshold range so the stops sit at
 * their real position on the track.
 */

(() => {
  const REVEAL_DURATION = 1000;
  const CELEBRATION_DURATION = 1200;
  const STOP_POP_DURATION = 700;
  const COMPLETE_THRESHOLD = 99.5;

  const rewardBarState = new WeakMap();
  const tierReachedState = new WeakMap();
  let drawerObserver = null;
  let observedDrawer = null;
  let updateScheduled = false;

  const prefersReducedMotion = () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  // The app stores the fill width in a hashed CSS variable on the tier element
  const readTierProgress = (tierElement) => {
    for (let index = 0; index < tierElement.style.length; index += 1) {
      const propertyName = tierElement.style[index];

      if (propertyName.startsWith('--')) {
        const propertyValue = tierElement.style.getPropertyValue(propertyName).trim();

        if (propertyValue.endsWith('%')) {
          return parseFloat(propertyValue);
        }
      }
    }

    const track = tierElement.querySelector('.ocu-reward-bar-tier__progress');

    if (!track || track.offsetWidth === 0) {
      return 0;
    }

    const fillWidth = parseFloat(window.getComputedStyle(track, '::before').width) || 0;

    return (fillWidth / track.offsetWidth) * 100;
  };

  // Tier components keep their props on the Vue instance attached to the root element
  const readTierThreshold = (tierElement) => {
    const tierComponent = tierElement.__vue__;
    const tierProps = tierComponent && tierComponent.$props ? tierComponent.$props.tier : null;
    const threshold = tierProps ? parseFloat(tierProps.threshold) : NaN;

    if (Number.isFinite(threshold)) {
      return threshold;
    }

    // Fall back to the last amount written in the tier headline, for example "Free shipping ($65)"
    const headline = tierElement.querySelector('.ocu-reward-bar--tier-headline');
    const amountMatches = headline ? headline.textContent.match(/\d[\d,]*(\.\d+)?/g) : null;

    if (!amountMatches) {
      return NaN;
    }

    return parseFloat(amountMatches[amountMatches.length - 1].replace(/,/g, ''));
  };

  const sizeTiersByThreshold = (tiers) => {
    const thresholds = tiers.map(readTierThreshold);
    let previousThreshold = 0;

    const ranges = thresholds.map((threshold) => {
      const range = threshold - previousThreshold;
      previousThreshold = threshold;

      return range;
    });

    const hasValidRanges = ranges.every((range) => {
      return Number.isFinite(range) && range > 0;
    });

    tiers.forEach((tierElement, index) => {
      const flexGrow = hasValidRanges ? String(ranges[index]) : '';

      // Only write on change, writing the style attribute triggers the drawer observer
      if (tierElement.style.flexGrow !== flexGrow) {
        tierElement.style.flexGrow = flexGrow;
      }
    });
  };

  const playStopPop = (tierElement) => {
    if (prefersReducedMotion()) {
      return;
    }

    tierElement.classList.remove('is-just-reached');
    tierElement.getBoundingClientRect();
    tierElement.classList.add('is-just-reached');

    window.setTimeout(() => {
      tierElement.classList.remove('is-just-reached');
    }, STOP_POP_DURATION);
  };

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const state = rewardBarState.get(entry.target);

      if (!state) {
        return;
      }

      if (entry.isIntersecting && !state.isVisible) {
        state.isVisible = true;
        playReveal(entry.target, state);
      }

      if (!entry.isIntersecting) {
        state.isVisible = false;
      }
    });
  }, { threshold: 0.5 });

  const playReveal = (rewardBar, state) => {
    if (prefersReducedMotion()) {
      return;
    }

    window.clearTimeout(state.revealTimer);
    rewardBar.classList.remove('is-revealing');

    // Force a reflow so the animation restarts on every open
    rewardBar.getBoundingClientRect();

    rewardBar.classList.add('is-revealing');
    state.isRevealing = true;

    state.revealTimer = window.setTimeout(() => {
      rewardBar.classList.remove('is-revealing');
      state.isRevealing = false;

      if (state.pendingCelebration) {
        state.pendingCelebration = false;
        playCelebration(rewardBar, state);
      }
    }, REVEAL_DURATION);
  };

  const playCelebration = (rewardBar, state) => {
    if (prefersReducedMotion()) {
      return;
    }

    if (state.isRevealing) {
      state.pendingCelebration = true;
      return;
    }

    window.clearTimeout(state.celebrationTimer);
    rewardBar.classList.remove('is-celebrating');
    rewardBar.getBoundingClientRect();
    rewardBar.classList.add('is-celebrating');

    state.celebrationTimer = window.setTimeout(() => {
      rewardBar.classList.remove('is-celebrating');
    }, CELEBRATION_DURATION);
  };

  const enhanceAccessibility = (rewardBar, overallProgress) => {
    const messageWrapper = rewardBar.querySelector('.ocu-reward-bar-message-wrapper');
    const progressList = rewardBar.querySelector('.ocu-reward-bar-progress');

    if (messageWrapper && !messageWrapper.hasAttribute('aria-live')) {
      messageWrapper.setAttribute('aria-live', 'polite');
    }

    if (progressList) {
      progressList.setAttribute('role', 'progressbar');
      progressList.setAttribute('aria-label', 'Cart rewards progress');
      progressList.setAttribute('aria-valuemin', '0');
      progressList.setAttribute('aria-valuemax', '100');
      progressList.setAttribute('aria-valuenow', String(Math.round(overallProgress)));
    }
  };

  const updateRewardBar = (rewardBar) => {
    let state = rewardBarState.get(rewardBar);

    if (!state) {
      state = {
        isComplete: null,
        isVisible: false,
        isRevealing: false,
        pendingCelebration: false,
        revealTimer: null,
        celebrationTimer: null
      };
      rewardBarState.set(rewardBar, state);
      revealObserver.observe(rewardBar);
    }

    const tiers = Array.from(rewardBar.querySelectorAll('.ocu-reward-bar-tier'));

    if (tiers.length === 0) {
      return;
    }

    sizeTiersByThreshold(tiers);

    let progressTotal = 0;
    let reachedCount = 0;
    let hasNextTier = false;

    tiers.forEach((tierElement) => {
      const tierProgress = Math.min(readTierProgress(tierElement), 100);
      const isReached = tierProgress >= COMPLETE_THRESHOLD;
      const wasReached = tierReachedState.get(tierElement);
      const isNext = !isReached && !hasNextTier;

      tierElement.classList.toggle('is-reached', isReached);
      tierElement.classList.toggle('is-next', isNext);
      progressTotal += tierProgress;

      if (isNext) {
        hasNextTier = true;
      }

      if (isReached) {
        reachedCount += 1;
      }

      // Only pop a stop on a transition, never the state found on first render
      if (wasReached === false && isReached) {
        playStopPop(tierElement);
      }

      tierReachedState.set(tierElement, isReached);
    });

    const isComplete = reachedCount === tiers.length;

    rewardBar.classList.toggle('is-complete', isComplete);
    enhanceAccessibility(rewardBar, progressTotal / tiers.length);

    // Only celebrate a transition, never the state found on first render
    if (state.isComplete === false && isComplete) {
      playCelebration(rewardBar, state);
    }

    state.isComplete = isComplete;
  };

  const updateAll = () => {
    updateScheduled = false;
    attachDrawerObserver();
    document.querySelectorAll('.ocu-cart-drawer .ocu-reward-bar').forEach(updateRewardBar);
  };

  const scheduleUpdate = () => {
    if (updateScheduled) {
      return;
    }

    updateScheduled = true;
    window.requestAnimationFrame(updateAll);
  };

  // Tier style changes only happen inside the drawer, so attribute changes are observed there only
  const attachDrawerObserver = () => {
    const drawer = document.querySelector('.ocu-cart-drawer');

    if (!drawer || drawer === observedDrawer) {
      return;
    }

    if (drawerObserver) {
      drawerObserver.disconnect();
    }

    observedDrawer = drawer;
    drawerObserver = new MutationObserver(scheduleUpdate);
    drawerObserver.observe(drawer, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style']
    });
  };

  const documentObserver = new MutationObserver(scheduleUpdate);
  documentObserver.observe(document.body, { childList: true, subtree: true });

  scheduleUpdate();
})();
